import { apiClient, socialApiClient } from "@/lib/axios";
import { getTurnstileToken } from "@/lib/turnstile";
import {
  ChallengeCampaign,
  ChallengeCampaignWithTotal,
  ChallengeCampaignStatus,
  ChallengeUser,
  ChallengeMetadata,
  GetAllCampaignsResponse,
  WithPagination,
  LeaderboardPeriodType,
} from "@/types";
import { SocialUser } from "@/types/social-pay.types";
import { CreateTelegramUser } from "@/components/socials/telegram-login-button";
import FingerprintJS from "@fingerprintjs/fingerprintjs";

const getDeviceSignature = async () => {
  try {
    const fp = await FingerprintJS.load();
    const result = await fp.get();
    return result.visitorId; // Unique visitor identifier
  } catch (error) {
    console.error("Error fetching device signature:", error);
    return null;
  }
};

export class PulsarService {
  static async getChallengeCampaigns() {
    const { data } = await apiClient.get<ChallengeCampaignWithTotal[]>(`/pulsar/challenges`);
    return data;
  }

  static async getChallengeCampaign(id: string, groupId?: string) {
    const url = groupId ? `/pulsar/challenges/${id}/${groupId}` : `/pulsar/challenges/${id}`;
    const { data } = await apiClient.get<ChallengeCampaign>(url);
    return data;
  }

  static async getChallengeCampaignTaskStatus(id: string, groupId?: string) {
    const url = groupId ? `/pulsar/challenges/${id}/tasks-status/${groupId}` : `/pulsar/challenges/${id}/tasks-status`;
    
    // Add device signature to help with user identification
    const results = await Promise.allSettled([getDeviceSignature()]);
    const deviceSignature = results[0].status === "fulfilled" ? results[0].value : undefined;
    
    const { data } = await apiClient.get<ChallengeCampaignStatus>(url, {
      headers: deviceSignature ? { "X-Device-Signature": deviceSignature } : undefined,
    });
    
    return data;
  }

  static async getChallengeCampaignLeaderboard(
    campaignId: string,
    groupId: string | undefined,
    page: number,
    limit: number,
    period: LeaderboardPeriodType = LeaderboardPeriodType.ALL,
    date?: string
  ) {
    let url = `/pulsar/challenges/${campaignId}/leaderboard?page=${page}&limit=${limit}`;
    if (groupId) {
      url = `/pulsar/challenges/${campaignId}/leaderboard/${groupId}?page=${page}&limit=${limit}`;
    }
    if (period !== LeaderboardPeriodType.ALL) {
      url += `&type=${period}`;
    }
    if (date) {
      url += `&date=${date}`;
    }
    const { data } = await apiClient.get<{ count: number; data: Array<{
      address: string;
      campaignId: string;
      campaignGroup: string;
      totalPoints: string;
      rank: number;
      isVerified: boolean;
      hasPremiumSubscription: boolean;
    }> }>(url);
    return data;
  }

  static async getChallengeCampaignUser(campaignId: string, groupId?: string) {
    const results = await Promise.allSettled([getDeviceSignature()]);
    const deviceSignature = results[0].status === "fulfilled" ? results[0].value : undefined;

    const url = groupId ? `/pulsar/challenges/${campaignId}/me/${groupId}` : `/pulsar/challenges/${campaignId}/me`;

    const { data } = await apiClient.get<ChallengeUser>(url, {
      headers: deviceSignature ? { "X-Device-Signature": deviceSignature } : undefined,
    });
    return data;
  }

  static async getChallengeCampaignMetadata(id: string, groupId?: string) {
    const url = groupId ? `/pulsar/challenges/${id}/metadata/${groupId}` : `/pulsar/challenges/${id}/metadata`;
    const { data } = await apiClient.get<ChallengeMetadata>(url);
    return data;
  }

  static async getAllCampaigns() {
    const { data } = await apiClient.get<GetAllCampaignsResponse>(`/pulsar/challenges/all`);
    return data;
  }

  static async getAllCampaignsByGroup(group: string) {
    const { data } = await apiClient.get<GetAllCampaignsResponse>(`/pulsar/challenges/all/${group}`);
    return data;
  }

  static async getCampaign(campaignId: string, groupId?: string) {
    const url = groupId ? `/pulsar/challenges/${campaignId}/${groupId}` : `/pulsar/challenges/${campaignId}`;
    const { data } = await apiClient.get<ChallengeCampaign>(url);
    return data;
  }

  static doCampaignTask = async (taskGuid: string, extraArguments?: string[]) => {
    const turnstileToken = await getTurnstileToken();
    return apiClient.post<{ status: boolean; error?: string }>(
      `/pulsar/challenges/do-task`,
      { taskGuid, extraArguments: extraArguments || [] },
      { headers: { "x-turnstile-token": turnstileToken } }
    );
  };


  // Social Authentication Methods (using different API endpoint)
  static getSocialUser = async () => {
    return socialApiClient.get<SocialUser>(`/social-pay/me`);
  };

  static registerTwitter = async (redirectUrl?: string) => {
    return socialApiClient.post<string>("/social-pay/register/twitter", {
      type: "register",
      redirectUrl,
    });
  };

  static registerDiscord = async (redirectUrl: string) => {
    return socialApiClient.get<string>(
      `/social-pay/register/discord?redirectUri=${redirectUrl}`
    );
  };

  static registerTelegram = async (user: CreateTelegramUser) => {
    return socialApiClient.post<string>("/social-pay/register/telegram", user);
  };

  static unlinkTwitter = async () => {
    return socialApiClient.delete<string>("/social-pay/unregister");
  };

  static unregisterDiscord = async () => {
    return socialApiClient.delete("/social-pay/register/discord");
  };

  static unlinkTelegram = async () => {
    return socialApiClient.delete("/social-pay/register/telegram");
  };

  static async createReferral(
    campaignId: string,
    groupId: string | undefined,
    referralCode: string
  ) {
    const results = await Promise.allSettled([getDeviceSignature()]);
    const deviceSignature = results[0].status === "fulfilled" ? results[0].value : undefined;

    const url = groupId 
      ? `/pulsar/challenges/${campaignId}/create-referral/${groupId}`
      : `/pulsar/challenges/${campaignId}/create-referral`;

    const { data } = await apiClient.post<{ success: boolean }>(url, 
      { referralCode },
      {
        headers: deviceSignature ? { "X-Device-Signature": deviceSignature } : undefined,
      }
    );
    return data;
  }

  static async getReferrals(
    campaignId: string,
    groupId: string | undefined,
    address: string,
    page: number,
    limit: number
  ) {
    const results = await Promise.allSettled([getDeviceSignature()]);
    const deviceSignature = results[0].status === "fulfilled" ? results[0].value : undefined;

    let url = `/pulsar/challenges/${campaignId}`;
    if (groupId) {
      url += `/${groupId}`;
    }
    url += `/referrals?page=${page}&limit=${limit}`;

    const { data } = await apiClient.get<{
      count: number;
      data: ChallengeUser[];
    }>(url, {
      headers: {
        ...(deviceSignature ? { "X-Device-Signature": deviceSignature } : {}),
        'X-User-Address': address,
      },
    });
    return data;
  }
}
